import * as SelectPrimitive from "@radix-ui/react-select";
import { UUID } from "@bitory-gmbh/nidavellir";
import { VDI6022 } from "../../report/types/report";
import {
  updateHtzReportCache,
  PatchRequest,
  useHtzReportPatchMutation,
  useHtzReportShowQuery,
} from "../../report/api/report-api";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { TextInput } from "@/shared/components/form/text-input";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { DateInput } from "@/shared/components/form/date-input";
import { useHtzAirHandlingUnitDataShowQuery } from "../api/ahu-data-api";
import { Separator } from "@/shared/components/ui/separator";
import { ComponentProps } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Check, Minus, X } from "lucide-react";
import { Field, FieldLabel } from "@/shared/components/ui/field";

export function ReportBaseDataCard({
  id,
  disabled,
}: {
  id: UUID;
  disabled?: boolean;
}) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useHtzReportShowQuery({ id });

  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMutation(),
  );
  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  const { data: ahuData } = useHtzAirHandlingUnitDataShowQuery(
    { id: data?.ahuDataId ?? "" },
    { skip: !data || !data.ahuDataId },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Anlagenbericht")}</CardTitle>
        <CardDescription>{t("Stammdaten zum Bericht")}</CardDescription>
      </CardHeader>
      <CardContent>
        {dataError && <RTKQueryErrorAlert error={dataError} />}
        {dataLoading && <CardContentSkeleton />}
        {data && (
          <div className="grid grid-cols-6 gap-4">
            <TextInput
              label={t("RLT-Anlage")}
              field="airHandlingUnitId"
              text={ahuData?.designation ?? ""}
              onTextChange={() => {}}
              errors={null}
              className="col-span-2"
              disabled
            />
            <TextInput
              label={t("Bericht Author")}
              field="createdBy"
              text={data.createdBy}
              onTextChange={(createdBy) => {
                resetNotReadyErrorField("createdBy");
                set({ ...request, createdBy }, { ...data, createdBy });
              }}
              errors={inputValidationErrorMessages(
                error || notReadyError,
                "createdBy",
              )}
              className="col-span-2"
              disabled={disabled}
            />
            <DateInput
              label={t("Bericht Datum")}
              date={data.date ?? null}
              onDateChange={(date) => {
                resetNotReadyErrorField("date");
                set({ ...request, date }, { ...data, date });
              }}
              errors={inputValidationErrorMessages(
                error || notReadyError,
                "date",
              )}
              className="col-span-2"
              disabled={disabled}
            />
            <TextInput
              label={t("Inspektion durchgeführt von")}
              field="inspectionConductedBy"
              text={data.inspectionConductedBy}
              onTextChange={(inspectionConductedBy) => {
                resetNotReadyErrorField("inspectionConductedBy");
                set(
                  { ...request, inspectionConductedBy },
                  { ...data, inspectionConductedBy },
                );
              }}
              errors={inputValidationErrorMessages(
                error || notReadyError,
                "inspectionConductedBy",
              )}
              className="col-span-2"
              disabled={disabled}
            />
            <TextInput
              label={t("Begleitendes Personal")}
              field="inspectionConductedByStaff"
              text={data.inspectionConductedByStaff}
              onTextChange={(inspectionConductedByStaff) => {
                resetNotReadyErrorField("inspectionConductedByStaff");
                set(
                  { ...request, inspectionConductedByStaff },
                  { ...data, inspectionConductedByStaff },
                );
              }}
              errors={inputValidationErrorMessages(
                error || notReadyError,
                "inspectionConductedByStaff",
              )}
              className="col-span-4"
              disabled={disabled}
            />
            <DateInput
              label={t("Letzte Inspektion")}
              date={data.lastInspectionDate ?? null}
              onDateChange={(lastInspectionDate) => {
                resetNotReadyErrorField("lastInspectionDate");
                set(
                  { ...request, lastInspectionDate },
                  { ...data, lastInspectionDate },
                );
              }}
              errors={inputValidationErrorMessages(
                error || notReadyError,
                "lastInspectionDate",
              )}
              className="col-span-2"
              disabled={disabled}
            />
            <DateInput
              label={t("Inspektionsdatum")}
              date={data.inspectionDate ?? null}
              onDateChange={(inspectionDate) => {
                resetNotReadyErrorField("inspectionDate");
                set(
                  { ...request, inspectionDate },
                  { ...data, inspectionDate },
                );
              }}
              errors={inputValidationErrorMessages(
                error || notReadyError,
                "inspectionDate",
              )}
              className="col-span-2"
              disabled={disabled}
            />
            <DateInput
              label={t("Nächste Inspektion")}
              date={data.nextInspectionDate ?? null}
              onDateChange={(nextInspectionDate) => {
                resetNotReadyErrorField("nextInspectionDate");
                set(
                  { ...request, nextInspectionDate },
                  { ...data, nextInspectionDate },
                );
              }}
              errors={inputValidationErrorMessages(
                error || notReadyError,
                "nextInspectionDate",
              )}
              className="col-span-2"
              disabled={disabled}
            />
            <Separator className="col-span-6" />
            <Field className="col-span-6">
              <FieldLabel>{"Gesamtbeurteilung"}</FieldLabel>
              <VDI6022JudgementSelect
                value={data.vdi6022Judgement}
                onValueChange={(vdi6022Judgement) =>
                  set(
                    {
                      ...request,
                      vdi6022Judgement: vdi6022Judgement as VDI6022,
                    },
                    { ...data, vdi6022Judgement: vdi6022Judgement as VDI6022 },
                  )
                }
              />
            </Field>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VDI6022JudgementSelect({
  ...props
}: ComponentProps<typeof SelectPrimitive.Root>) {
  return (
    <Select {...props}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder={t("Ergebnis auswählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={VDI6022.Zero}>
            <Minus className="mr-2" />
            <span>{t("Noch nicht beurteilt")}</span>
          </SelectItem>
          <SelectItem value={VDI6022.Passed}>
            <Check className="text-primary mr-2 font-bold" />
            <span className="text-primary font-bold">{t(VDI6022.Passed)}</span>
          </SelectItem>
          <SelectItem value={VDI6022.Failed}>
            <X className="text-destructive mr-2 font-bold" />
            <span className="text-destructive font-bold">
              {t(VDI6022.Failed)}
            </span>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
